<script setup lang="ts">
// §3.1 scenario 2: a raw TCP "port console" to an agent behind NAT. Pick a
// relay-enabled agent + port; we open /ws/relay and bridge it to an xterm —
// keystrokes go out as raw bytes, incoming bytes are written to the terminal.
// Best for text protocols / debug ports; binary protocols (ssh, TLS) won't
// render readably (use the SSH-to-agent session for shells).
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import { useI18n } from "vue-i18n";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

import { useMachinesStore } from "@/stores/machines";

const { t } = useI18n();
const machines = useMachinesStore();

const target = ref("");
const port = ref(8080);
const connected = ref(false);
const status = ref<"idle" | "connecting" | "open" | "closed">("idle");

const termEl = ref<HTMLDivElement | null>(null);
const term = shallowRef<Terminal | null>(null);
const fit = shallowRef<FitAddon | null>(null);
let socket: WebSocket | null = null;

onMounted(() => void machines.start());
onBeforeUnmount(() => {
  disconnect();
  machines.stop();
});

// Only agents that can actually be relayed into.
const targets = computed(() =>
  machines.agents.filter((a) => a.presence === "online" && a.relay_enabled),
);

function xtermTheme() {
  const css = getComputedStyle(document.documentElement);
  return {
    background: css.getPropertyValue("--color-bg").trim() || "#0d1117",
    foreground: css.getPropertyValue("--color-fg").trim() || "#e6edf3",
  };
}

function connect() {
  if (connected.value || !target.value) return;
  connected.value = true;
  status.value = "connecting";

  // Defer one tick so the terminal container is in the DOM.
  requestAnimationFrame(() => {
    if (!termEl.value) return;
    const term1 = new Terminal({
      convertEol: true,
      cursorBlink: true,
      fontSize: 13,
      scrollback: 4000,
      fontFamily: 'ui-monospace, "JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
      theme: xtermTheme(),
    });
    const fit1 = new FitAddon();
    term1.loadAddon(fit1);
    term1.open(termEl.value);
    fit1.fit();
    term.value = term1;
    fit.value = fit1;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws/relay?target_agent_id=${encodeURIComponent(
      target.value,
    )}&port=${port.value}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    socket = ws;

    ws.onopen = () => {
      status.value = "open";
    };
    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) term1.write(new Uint8Array(ev.data));
    };
    ws.onclose = () => {
      status.value = "closed";
      term1.writeln("\r\n\x1b[2m-- disconnected --\x1b[0m");
    };
    // Keystrokes → raw bytes (one stream, no framing on this socket).
    term1.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(new TextEncoder().encode(data));
    });
  });
}

// Open the agent's web service (HTTP) via the reverse-proxy in a new tab.
function openWebUi() {
  if (!target.value) return;
  window.open(`/proxy/${target.value}/${port.value}/`, "_blank", "noopener");
}

function disconnect() {
  if (socket) {
    socket.close();
    socket = null;
  }
  if (term.value) {
    term.value.dispose();
    term.value = null;
  }
  connected.value = false;
  status.value = "idle";
}
</script>

<template>
  <section class="relay">
    <h1>{{ t("relay_console.title") }}</h1>

    <form v-if="!connected" class="relay__form" @submit.prevent="connect">
      <label>
        <span>{{ t("relay_console.target") }}</span>
        <select v-model="target" required>
          <option value="">{{ t("relay_console.target_placeholder") }}</option>
          <option v-for="a in targets" :key="a.id" :value="a.id">
            {{ a.display_name || a.hostname }} ({{ a.login }})
          </option>
        </select>
      </label>
      <label>
        <span>{{ t("relay_console.port") }}</span>
        <input v-model.number="port" type="number" min="1" max="65535" required />
      </label>
      <div class="relay__actions">
        <button type="submit" class="relay__connect" :disabled="!target">
          {{ t("relay_console.connect") }}
        </button>
        <button
          type="button"
          class="relay__web"
          :disabled="!target"
          @click="openWebUi"
        >
          {{ t("relay_console.open_web") }}
        </button>
      </div>
      <p v-if="targets.length === 0" class="relay__hint">{{ t("relay_console.none") }}</p>
      <p class="relay__hint">{{ t("relay_console.hint") }}</p>
      <p class="relay__hint">{{ t("relay_console.web_hint") }}</p>
    </form>

    <div v-else class="relay__live">
      <header class="relay__bar">
        <span class="relay__status">{{ t(`relay_console.status_${status}`) }}</span>
        <span class="relay__spacer"></span>
        <button type="button" class="relay__disconnect" @click="disconnect">
          {{ t("relay_console.disconnect") }}
        </button>
      </header>
      <div ref="termEl" class="relay__pane"></div>
    </div>
  </section>
</template>

<style scoped>
.relay {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  height: 100%;
}
.relay h1 {
  margin: 0;
}
.relay__form {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-width: 28rem;
}
.relay__form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}
.relay__form select,
.relay__form input {
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-size: 0.95rem;
}
.relay__actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.relay__connect {
  padding: 0.55rem 1rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.relay__web {
  padding: 0.55rem 1rem;
  background: var(--color-bg-elev);
  color: var(--color-fg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
}
.relay__connect:disabled,
.relay__web:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.relay__hint {
  margin: 0;
  font-size: 0.78rem;
  color: var(--color-fg-dim);
}
.relay__live {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
}
.relay__bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
}
.relay__status {
  color: var(--color-fg-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 0.72rem;
}
.relay__spacer {
  flex: 1;
}
.relay__disconnect {
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  border-radius: 4px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elev);
  color: var(--color-fg);
  cursor: pointer;
}
.relay__pane {
  flex: 1;
  min-height: 0;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.4rem;
  overflow: hidden;
}
</style>
